/**
 * The server's "waiting on you" items (`GET /api/v1/me/waiting`, ADR-0070)
 * as notifications: what to call each one, where it leads, and which icon
 * family it belongs to. One mapping, used by the bell, the Notifications page
 * and the Home page, so the three always say the same thing.
 */

import { TICKETS_PATH } from "@/features/admin/ticketsPath";
import type { WaitingItem } from "@/shared/api/waiting";
import type { AppNotification, NotificationKind } from "./useNotifications";

const DOCUMENT: Record<string, string> = {
  requirements: "Requirements Specification",
  plan: "Release Validation Plan",
  test_plan: "Test Plan",
  report: "Validation Report",
};

/** The owner's next step at a gate (`waiting_service._owner_gate`). */
const GATE_STEP: Record<string, string> = {
  submit_risks: "Submit the risks for approval (gate 1)",
  confirm_risks: "Confirm the risks (gate 1)",
  approve_tests: "Approve tests & assign testers (gate 2)",
  sign_report: "Report awaiting your sign-off (gate 3)",
  reopen: "Rejected, reopen to retest",
};

/** Which icon and colour family a kind belongs to (`notificationMeta`). */
const FAMILY: Record<string, NotificationKind> = {
  validation_gate: "gate",
  validation_risk_approval: "sign",
  validation_document_approval: "sign",
  validation_signoff: "sign",
  validation_qa_acceptance: "sign",
  validation_document_review: "review",
  validation_test: "test",
  pipeline_approval: "approval",
  veeva_approval: "approval",
  support_ticket: "ticket",
};

/** A ticket's question can be long; the row shows its beginning. */
const MAX_SUBJECT = 140;

function short(text: string | null | undefined): string {
  const clean = (text ?? "").trim().replace(/\s+/g, " ");
  return clean.length > MAX_SUBJECT ? `${clean.slice(0, MAX_SUBJECT - 1)}…` : clean;
}

function by(i: WaitingItem): string {
  return i.by_display_name ? ` · from ${i.by_display_name}` : "";
}

/** What is wanted of the person — the row's second line. */
export function waitingDetail(i: WaitingItem): string {
  const doc = DOCUMENT[i.document_kind ?? ""] ?? "document";
  switch (i.kind) {
    case "validation_gate":
      return GATE_STEP[i.step ?? ""] ?? "Waiting on you";
    case "validation_risk_approval":
      return "Sign the risk assessment (gate 1)";
    case "validation_document_review":
      return `Review the ${doc}`;
    case "validation_document_approval":
      return `Approve the ${doc}`;
    case "validation_signoff":
      return "Your turn to sign the validation package";
    case "validation_qa_acceptance":
      return i.count === 1
        ? "1 accepted test to counter-sign"
        : `${i.count ?? 0} accepted tests to counter-sign`;
    case "validation_test":
      return short(i.subject);
    case "pipeline_approval":
      return `Approval needed: ${short(i.subject)}${by(i)}`;
    case "veeva_approval":
      return `Approve a Veeva user change${by(i)}`;
    case "support_ticket":
      return `“${short(i.subject)}”${by(i)}`;
    default:
      return "Waiting on you";
  }
}

/** Where the row leads. */
export function waitingLink(i: WaitingItem): string {
  switch (i.kind) {
    case "validation_test":
      return "/validation/my-tests";
    case "pipeline_approval":
      return "/projects/approvals";
    case "support_ticket":
      return `${TICKETS_PATH}/${encodeURIComponent(i.resource_id)}`;
    case "veeva_approval":
      return `/admin/approvals/veeva/${encodeURIComponent(i.resource_id)}`;
    default:
      return i.resource_type === "validation_run"
        ? `/validation/runs/${encodeURIComponent(i.resource_id)}`
        : "/notifications";
  }
}

export function waitingNotification(i: WaitingItem): AppNotification {
  return {
    id: i.id,
    kind: FAMILY[i.kind] ?? "gate",
    title: i.title,
    detail: waitingDetail(i),
    status: i.status ?? i.kind,
    at: i.since_utc ?? null,
    to: waitingLink(i),
  };
}

/** Every item, in the server's order (newest first). */
export function waitingNotifications(items: WaitingItem[]): AppNotification[] {
  return items.map(waitingNotification);
}
